import { useRef, useState } from 'react';
import * as v from 'valibot';

import { Button, DataTable, Icon, Modal, Pagination, Radio, RadioGroup, Spinner, Text } from '@nvision/ui';

import { NoData } from '@/modules/errors';

import { useCarriers } from '../../../hooks';
import {
  type Carrier,
  type CarrierFilters,
  type ProviderSearchFormValues,
  type ProviderSearchParams,
  providerSearchParamsSchema,
} from '../../../models';
import * as countriesService from '../../../services/countries.service';
import { ProviderSearchForm, type SearchFormRef } from '../provider-search-form';

import styles from './provider-search-modal.module.css';

interface ProviderSearchModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectionChange: (values: Carrier[]) => void;
}

type ProviderSearchFilters = Partial<CarrierFilters> & ProviderSearchParams;

export const ProviderSearchModal: React.FC<ProviderSearchModalProps> = ({
  isOpen = false,
  setIsOpen,
  onSelectionChange,
}) => {
  const [selectedCarriers, setSelectedCarriers] = useState<Carrier[]>([]);

  const [filters, setFilters] = useState<ProviderSearchFilters>(v.getDefaults(providerSearchParamsSchema));
  const [sorting, setSorting] = useState<boolean>(false);
  const { data: carriers, isFetching } = useCarriers(filters);

  const searchFormRef = useRef<SearchFormRef>({} as SearchFormRef);

  const reset = () => {
    setSelectedCarriers([]);
    setFilters(v.getDefaults(providerSearchParamsSchema));
  };

  const close = () => {
    searchFormRef.current?.reset();
    setSelectedCarriers([]);
    setIsOpen(false);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(value) => setIsOpen(value)} className={styles.modal}>
      <Modal.Header className={styles.header}>
        <div className={styles.headerContent}>
          <Text size="xlg" weight="semibold">
            Provider Search
          </Text>
          <Icon name="x" cursor="pointer" onClick={close} />
        </div>
        <Text className={styles.headerDescription}>You may find and select active transportation providers only</Text>
      </Modal.Header>
      <Modal.Content className={styles.content}>
        <ProviderSearchForm
          formRef={searchFormRef}
          handleReset={reset}
          handleSubmit={(values: ProviderSearchFormValues) =>
            setFilters({
              limit: filters.limit,
              page: 1,
              search: {
                carrierName: values.additional.companyName,
                carrierScacCode: values.additional.scac,
                countryCode: values.country.id,
                postalCode: values.additional.portPostal,
              },
              sorting: `carrierName ${sorting ? 'desc' : 'asc'}`,
            })
          }
        />
        {isFetching ? (
          <div className={`${styles.tableContainer} ${styles.loader}`}>
            <Spinner size="lg" />
          </div>
        ) : carriers.list.length ? (
          <div>
            <div className={styles.total}>
              {carriers.total === 1 ? (
                <>
                  <Text weight="bold">{`${carriers.total} provider `}</Text>is found
                </>
              ) : (
                <>
                  <Text weight="bold">{`${carriers.total} providers `}</Text>are found
                </>
              )}
            </div>
            <div className={styles.tableContainer}>
              <DataTable
                columns={[
                  {
                    accessorKey: 'id',
                    cell: (props) => (
                      <RadioGroup
                        value={selectedCarriers[0]?.id === props.row.original.id ? props.row.original.id : ''}
                        onChange={() => setSelectedCarriers([props.row.original])}
                        className={styles.radio}
                      >
                        <Radio value={props.row.original.id} className={styles.radio} />
                      </RadioGroup>
                    ),
                    enableSorting: false,
                    header: '',
                    maxSize: 0,
                  },
                  {
                    accessorKey: 'carrierName',
                    cell: (props) => props.row.original.carrierName,
                    header: 'Provider Name',
                    sortingFn: () => 0,
                  },
                  {
                    accessorKey: 'carrierScacCode',
                    cell: (props) => props.row.original.carrierScacCode,
                    enableSorting: false,
                    header: 'SCAC',
                  },
                  {
                    accessorKey: 'carrierAddr1',
                    cell: (props) => props.row.original.carrierAddr1,
                    enableSorting: false,
                    header: 'Address',
                  },
                  {
                    accessorKey: 'carrierAddr2',
                    cell: (props) => props.row.original.carrierAddr2,
                    enableSorting: false,
                    header: 'Address 2',
                  },
                  {
                    accessorKey: 'carrierCity',
                    cell: (props) => props.row.original.carrierCity,
                    enableSorting: false,
                    header: 'City',
                  },
                  {
                    accessorKey: 'carrierState',
                    cell: (props) => props.row.original.carrierState,
                    enableSorting: false,
                    header: 'State',
                  },
                  {
                    accessorKey: 'postalCode',
                    cell: (props) => props.row.original.postalCode,
                    enableSorting: false,
                    header: 'Postal',
                  },
                  {
                    accessorKey: 'countryCode',
                    cell: (props) => {
                      const countryCode = props.row.original.countryCode ?? '';
                      return countriesService.getAll()?.map[countryCode]?.label ?? countryCode;
                    },
                    enableSorting: false,
                    header: 'Country',
                  },
                ]}
                data={carriers.list}
                page={filters.page}
                limit={filters.limit}
                totalItems={carriers.total}
                sorting={[
                  {
                    desc: sorting,
                    id: 'carrierName',
                  },
                ]}
                onSortingChange={() => {
                  const isDescending = !sorting;
                  setSorting(isDescending);
                  setFilters((prev) => ({
                    ...prev,
                    page: 1,
                    sorting: `carrierName ${isDescending ? 'desc' : 'asc'}`,
                  }));
                }}
              />
            </div>

            <Pagination
              className={styles.pagination}
              totalItems={carriers.total}
              limit={filters.limit}
              page={filters.page}
              onPageChange={(page: number) => setFilters((prev) => ({ ...prev, page }))}
              onOffsetChange={(offset: number) =>
                setFilters((prev) => ({ ...prev, page: Math.floor(offset / prev.limit) + 1 }))
              }
              onLimitChange={(limit: number) => setFilters((prev) => ({ ...prev, limit, page: 1 }))}
            />
          </div>
        ) : (
          <NoData
            description="No data to display. Please select a Country and at least one of the following fields: Company Name, SCAC, or Port Postal."
            className={styles.noData}
          />
        )}
      </Modal.Content>
      <Modal.Footer className={styles.footer}>
        <Button outline secondary onClick={close}>
          Cancel
        </Button>
        <Button isDisabled={selectedCarriers.length === 0} onClick={() => onSelectionChange(selectedCarriers)}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
